/**
 * 国际化工具函数
 */

// 语言检测缓存
let languageCache = {
  language: null,
  url: null,
  timestamp: 0
}

// 缓存有效期（毫秒）
const CACHE_DURATION = 5000 // 5秒缓存

/**
 * 从 URL 参数中获取语言设置（优化版本）
 * 只有当 URL 中包含 lang=en 参数时才返回英文，否则返回中文
 * 实现了缓存机制，确保在100ms内完成
 * @returns {string} 语言代码 ('zh-CN' 或 'en-US')
 */
export function getLanguageFromURL() {
  const currentUrl = window.location.search
  const currentTime = Date.now()
  
  // 检查缓存是否有效
  if (languageCache.language && 
      languageCache.url === currentUrl && 
      (currentTime - languageCache.timestamp) < CACHE_DURATION) {
    return languageCache.language
  }
  
  try {
    // 优化：直接使用正则表达式检测，避免创建URLSearchParams对象
    const langMatch = currentUrl.match(/[?&]lang=([^&]*)/i)
    const langParam = langMatch ? langMatch[1] : null
    
    // 只有参数值为 'en' 时返回英文，其他情况都返回中文
    const language = langParam === 'en' ? 'en-US' : 'zh-CN'
    
    // 更新缓存
    languageCache = {
      language,
      url: currentUrl,
      timestamp: currentTime
    }
    
    return language
  } catch (error) {
    console.warn('获取URL语言参数失败:', error)
    
    // 即使出错也要更新缓存，避免重复错误
    const defaultLanguage = 'zh-CN'
    languageCache = {
      language: defaultLanguage,
      url: currentUrl,
      timestamp: currentTime
    }
    
    return defaultLanguage
  }
}

/**
 * 判断当前是否为英文模式
 * @returns {boolean} 是否为英文模式
 */
export function isEnglish() {
  return getLanguageFromURL() === 'en-US'
}

/**
 * 判断当前是否为中文模式
 * @returns {boolean} 是否为中文模式
 */
export function isChinese() {
  return getLanguageFromURL() === 'zh-CN'
}

/**
 * 获取当前语言设置
 * @returns {string} 当前语言代码
 */
export function getCurrentLanguage() {
  return getLanguageFromURL()
}

/**
 * 设置语言
 * @param {string} locale - 语言代码
 */
export function setLanguage(locale) {
  // 这个函数主要用于动态切换语言
  // 在当前需求中，语言主要通过 URL 参数控制
  // 但保留此函数以备将来扩展使用
  if (locale && (locale === 'zh-CN' || locale === 'en-US')) {
    updateHTMLLang(locale)
  }
}

/**
 * 更新 HTML lang 属性
 * @param {string} locale - 语言代码
 */
export function updateHTMLLang(locale) {
  try {
    if (document && document.documentElement) {
      // 设置 HTML 元素的 lang 属性
      document.documentElement.lang = locale
      
      // 也可以设置 dir 属性（文本方向），中英文都是从左到右
      document.documentElement.dir = 'ltr'
    }
  } catch (error) {
    console.warn('更新HTML lang属性失败:', error)
  }
}

/**
 * 获取语言显示名称
 * @param {string} locale - 语言代码
 * @returns {string} 语言显示名称
 */
export function getLanguageDisplayName(locale) {
  const displayNames = {
    'zh-CN': '中文',
    'en-US': 'English'
  }
  return displayNames[locale] || locale
}

/**
 * 检查是否支持指定语言
 * @param {string} locale - 语言代码
 * @returns {boolean} 是否支持该语言
 */
export function isSupportedLanguage(locale) {
  const supportedLanguages = ['zh-CN', 'en-US']
  return supportedLanguages.includes(locale)
}

/**
 * 清除语言检测缓存
 * 用于强制重新检测语言（例如URL变化时）
 */
export function clearLanguageCache() {
  languageCache = {
    language: null,
    url: null,
    timestamp: 0
  }
}

/**
 * 预热语言检测缓存
 * 在页面加载时调用，提前缓存语言设置
 */
export function warmupLanguageCache() {
  // 预热缓存，确保首次调用时性能最佳
  getLanguageFromURL()
}

/**
 * 获取语言检测性能统计
 * 用于开发环境性能监控
 * @returns {object} 性能统计信息
 */
export function getLanguageDetectionStats() {
  return {
    cacheHit: languageCache.language !== null,
    cacheAge: languageCache.timestamp ? Date.now() - languageCache.timestamp : 0,
    cachedLanguage: languageCache.language,
    cachedUrl: languageCache.url
  }
}